import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { ArrowLeft, CheckCircle, XCircle, AlertCircle, X } from "lucide-react";
import { GetSingleReviewThunk } from "../../redux/states/reviews/thunk";

const ViewReviews = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleReview, loading } = useSelector((state) => state.reviews);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mediaUrl, setMediaUrl] = useState("");
  const API_URL = process.env.REACT_APP_API_URL


  useEffect(() => {
    dispatch(GetSingleReviewThunk(uuid));
  }, [dispatch, uuid]);

  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-semibold text-blue-600">
              Loading review details...
            </p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  if (!singleReview) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
          <XCircle className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Review Not Found
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold"
          >
            Back
          </button>
        </div>
      </NonAuthLayout>
    );
  }

  const review = singleReview;
  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return { color: "bg-green-100 text-green-800", icon: CheckCircle };
      case "inactive":
        return { color: "bg-red-100 text-red-800", icon: XCircle };
      default:
        return { color: "bg-gray-100 text-gray-800", icon: AlertCircle };
    }
  };

  const StatusBadge = getStatusBadge(review.status);
  const StatusIcon = StatusBadge.icon;

  // Full media URL (adjust baseURL as per your API)
  const fullMediaUrl = review.file ? API_URL + review.file : null;

  const handleMediaClick = () => {
    if (fullMediaUrl) {
      setMediaUrl(fullMediaUrl);
      setIsModalOpen(true);
    }
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen py-1 px-1 sm:px-1 lg:px-1">
        <div className="max-w-12xl mx-auto space-y-1">
          {/* Header */}
          <div className="flex items-center justify-between bg-white p-2 rounded-xl shadow-sm border border-gray-200">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
            >
              <ArrowLeft />
              Back
            </button>
            <h4 className="text-small font-bold text-gray-800">
              Review Details
            </h4>
            <span
              className={`px-3 py-1 rounded-full flex items-center gap-1 ${StatusBadge.color}`}
            >
              <StatusIcon className="w-4 h-4" />
              {review.status}
            </span>
          </div>

          {/* Review Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 space-y-4">
            <h2 className="text-sm font-semibold text-gray-800">
              Review Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow label="UUID" value={review.uuid} />
              <InfoRow label="Title" value={review.title} />
              <InfoRow label="Comment" value={review.comment} />
              <InfoRow label="Rating" value={review.rating} />
              <InfoRow label="File Type" value={review.file_type} />
              <InfoRow label="Product Name" value={review.product_id_data} />
              <InfoRow label="User" value={review.user_id_data} />
            </div>

            {/* Media Preview */}
            {review.file && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Media
                </h3>
                <div
                  onClick={handleMediaClick}
                  className="cursor-pointer flex justify-start"
                >
                  {review.file_type === "image" ? (
                    <img
                      src={`${API_URL}/${review.file}`}
                      alt="Review"
                      className="max-h-60 rounded-lg border hover:scale-105 transition-transform duration-300"
                    />
                  ) : review.file_type === "video" ? (
                    <video
                     src={`${API_URL}/${review.file}`}
                      className="max-h-60 rounded-lg border hover:scale-105 transition-transform duration-300"
                      muted
                      controls
                    />
                  ) : (
                    <p className="text-gray-500">No media available</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="relative bg-white rounded-lg overflow-hidden max-w-4xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-3 right-3 text-gray-700 hover:text-red-500"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="flex justify-center p-4">
              {review.file_type === "image" ? (
                <img src={mediaUrl} alt="Full Preview" className="max-h-[80vh]" />
              ) : (
                <video
                  src={mediaUrl}
                  controls
                  autoPlay
                  className="max-h-[80vh]"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </NonAuthLayout>
  );
};

// ---------------- Helper Component ----------------
const InfoRow = ({ label, value }) => (
  <div className="flex justify-between bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
    <span className="font-medium text-gray-700">{label} </span>
    <span className="text-gray-900">{value || "—"}</span>
  </div>
);

export default ViewReviews;
